/**
 * PipelineAnimation Component
 *
 * v3.13 - 2026-02-14 - Fix #4/#10: Add idle wobble+pulse to discovery/transform particles
 * v3.11 - 2026-02-14 - Canvas particle animation for pipeline progress
 *   Inspired by pipeline-animation-v2.jsx (event-driven worker agents)
 *   Driven by real polling data from setupService.getStatus()
 *   Uses MyLojic brand colors (teal/navy/orange)
 *   v3.11b - Fixed sidebar to use phases prop for stage status, updated title text
 *   v3.11c - Fixed scrollbar (removed Workers/Legend sections, tightened gaps),
 *            phase dots + overlay now driven by phases prop like old MUI Stepper
 */
import React, { useRef, useEffect, useCallback, useState } from 'react';

// ── MyLojic Brand Colors ──
const COLORS = {
  teal: '#008080',
  tealLight: '#00A0A0',
  tealDark: '#006666',
  navy: '#003366',
  navyDark: '#002244',
  orange: '#F4A261',
  orangeDark: '#E89251',
  lightBlue: '#BBCDE2',
  success: '#4CAF50',
  error: '#F44336',
  warning: '#FF9800',
  // Data category colors (teal family + accent)
  financial: '#008080',
  operational: '#4CAF50',
  inventory: '#F4A261',
  contacts: '#BBCDE2',
  removed: '#F44336',
  worker: '#00A0A0',
};

const CATEGORY_KEYS = ['financial', 'operational', 'inventory', 'contacts'] as const;

const WORKER_NAMES = ['Agent-\u03B1', 'Agent-\u03B2', 'Agent-\u03B3', 'Agent-\u03B4'];

// ── Phase metadata with MyLojic colors ──
const PHASE_META: Record<string, { label: string; desc: string; color: string }> = {
  waiting: { label: 'Waiting', desc: 'Preparing pipeline...', color: '#94A3B8' },
  sync: { label: 'Syncing Data', desc: 'Syncing data from sources...', color: COLORS.teal },
  discovery: { label: 'Analyzing Structure', desc: 'Understanding your data schema...', color: COLORS.lightBlue },
  approval: { label: 'Review Proposals', desc: 'Waiting for your review...', color: COLORS.warning },
  transform: { label: 'Transforming', desc: 'Cleaning, enriching, and standardizing...', color: COLORS.orange },
  gold: { label: 'Building Insights', desc: 'Creating your dashboard KPIs...', color: COLORS.success },
  complete: { label: 'Complete', desc: 'Your data is ready.', color: COLORS.success },
  failed: { label: 'Error', desc: 'Something went wrong.', color: COLORS.error },
};

// Map backend phases to animation phases
const BACKEND_TO_ANIM: Record<string, string> = {
  sync: 'sync',
  discovery: 'discovery',
  approval: 'approval',
  transform: 'transform',
  gold: 'gold',
};

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * Math.min(1, Math.max(0, t));
}

function dist(x1: number, y1: number, x2: number, y2: number): number {
  return Math.sqrt((x1 - x2) ** 2 + (y1 - y2) ** 2);
}

// ── PARTICLE ──
class Particle {
  id: number;
  canvasW: number;
  canvasH: number;
  category: string;
  color: string;
  baseColor: string;
  radius: number;
  x: number;
  y: number;
  gridX: number;
  gridY: number;
  vx: number;
  vy: number;
  phase: string;
  opacity: number;
  settled: boolean;
  beingWorkedOn: boolean;
  workType: string | null;
  workDone: boolean;
  removed: boolean;
  scale: number;
  targetX: number;
  targetY: number;
  pulsePhase: number;
  wobble: number;
  wobbleSpeed: number;
  trail: { x: number; y: number }[];
  ringPulse: number;

  constructor(canvasW: number, canvasH: number, gridX: number, gridY: number, category: string, id: number) {
    this.id = id;
    this.canvasW = canvasW;
    this.canvasH = canvasH;
    this.category = category;
    this.color = (COLORS as any)[category] || COLORS.teal;
    this.baseColor = this.color;
    this.radius = 3.5 + Math.random() * 2;
    this.x = -20 - Math.random() * 300;
    this.y = 50 + Math.random() * (canvasH - 100);
    this.gridX = gridX;
    this.gridY = gridY;
    this.vx = 1.5 + Math.random() * 2.5;
    this.vy = (Math.random() - 0.5) * 1.5;
    this.phase = 'waiting';
    this.opacity = 0.75 + Math.random() * 0.25;
    this.settled = false;
    this.beingWorkedOn = false;
    this.workType = null;
    this.workDone = false;
    this.removed = false;
    this.scale = 1;
    this.targetX = gridX;
    this.targetY = gridY;
    this.pulsePhase = Math.random() * Math.PI * 2;
    this.wobble = Math.random() * Math.PI * 2;
    // v3.14 - Fix #4 follow-up: Increased wobble speed for visible animation (was 0.015-0.035, now 0.04-0.08)
    this.wobbleSpeed = 0.04 + Math.random() * 0.04;
    this.trail = [];
    this.ringPulse = 0;
  }

  update(dt: number) {
    this.wobble += this.wobbleSpeed;
    this.pulsePhase += 0.04;

    if (this.phase === 'sync') {
      // Streaming particles from left to right
      this.trail.push({ x: this.x, y: this.y });
      if (this.trail.length > 7) this.trail.shift();
      this.x += this.vx * dt * 60;
      this.y += this.vy * dt * 60;
      this.vy += (Math.random() - 0.5) * 0.08;
      this.vy *= 0.97;
      if (this.y < 40) this.vy = Math.abs(this.vy) * 0.5;
      if (this.y > this.canvasH - 40) this.vy = -Math.abs(this.vy) * 0.5;
      if (this.x > this.canvasW + 30) {
        this.x = -20 - Math.random() * 50;
        this.y = 50 + Math.random() * (this.canvasH - 100);
      }
    } else if (this.phase === 'discovery') {
      // v3.13 - Fix #4: Particles settle into grid, then gently wobble + pulse (not static)
      this.trail = [];
      if (!this.settled) {
        this.x = lerp(this.x, this.gridX, 0.06);
        this.y = lerp(this.y, this.gridY, 0.06);
        if (dist(this.x, this.y, this.gridX, this.gridY) < 0.5) {
          this.x = this.gridX;
          this.y = this.gridY;
          this.settled = true;
        }
      } else {
        // v3.14 - Fix #4 follow-up: Visible wobble + scan pulse after settling
        // Amplitude 3.0px (was 1.0), lerp 0.06 (was 0.03), opacity swing 0.3 (was 0.2)
        const wx = Math.sin(this.wobble) * 3.0;
        const wy = Math.cos(this.wobble * 0.7) * 3.0;
        this.x = lerp(this.x, this.gridX + wx, 0.06);
        this.y = lerp(this.y, this.gridY + wy, 0.06);
        this.opacity = 0.5 + Math.sin(this.pulsePhase * 0.6 + this.id * 0.15) * 0.3;
      }
    } else if (this.phase === 'approval') {
      // Gentle pulse while waiting for user
      const wx = Math.sin(this.wobble) * 1.2;
      const wy = Math.cos(this.wobble * 0.7) * 1.2;
      this.x = lerp(this.x, this.gridX + wx, 0.03);
      this.y = lerp(this.y, this.gridY + wy, 0.03);
      this.opacity = 0.6 + Math.sin(this.pulsePhase * 0.5) * 0.2;
    } else {
      // transform, gold, complete
      if (this.beingWorkedOn) {
        this.ringPulse += 0.1;
        if (this.workType === 'remove') {
          this.opacity = Math.max(0, this.opacity - 0.012);
          this.scale = Math.max(0, this.scale - 0.008);
          this.color = COLORS.removed;
          if (this.opacity <= 0) { this.removed = true; this.beingWorkedOn = false; }
        } else if (this.workType === 'move') {
          this.x = lerp(this.x, this.targetX, 0.03);
          this.y = lerp(this.y, this.targetY, 0.03);
          this.opacity = 0.6 + Math.sin(this.pulsePhase) * 0.3;
        } else if (this.workType === 'enrich') {
          this.scale = lerp(this.scale, 1.5, 0.02);
          this.opacity = 0.7 + Math.sin(this.pulsePhase * 2) * 0.25;
        } else if (this.workType === 'merge') {
          this.scale = Math.max(0, this.scale - 0.01);
          if (this.scale < 0.05) { this.removed = true; this.beingWorkedOn = false; }
        } else if (this.workType === 'validate') {
          this.opacity = lerp(this.opacity, 0.95, 0.03);
          this.scale = lerp(this.scale, 1, 0.03);
          this.color = this.baseColor;
        }
      } else if (this.workDone || this.phase === 'complete') {
        const wx = Math.sin(this.wobble) * 0.4;
        const wy = Math.cos(this.wobble * 0.7) * 0.4;
        this.x = lerp(this.x, this.targetX + wx, 0.03);
        this.y = lerp(this.y, this.targetY + wy, 0.03);
        this.opacity = lerp(this.opacity, 0.88, 0.02);
        this.scale = lerp(this.scale, 1, 0.02);
        this.color = this.baseColor;
        this.ringPulse = 0;
      } else {
        // v3.14 - Fix #10 follow-up: Visible idle drift for transform/gold particles
        // Amplitude 2.0px (was 0.6), lerp 0.04 (was 0.02), opacity swing 0.25 (was 0.15)
        const wx = Math.sin(this.wobble) * 2.0;
        const wy = Math.cos(this.wobble * 0.7) * 2.0;
        this.x = lerp(this.x, this.gridX + wx, 0.04);
        this.y = lerp(this.y, this.gridY + wy, 0.04);
        this.opacity = 0.55 + Math.sin(this.pulsePhase * 0.5 + this.id * 0.1) * 0.25;
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D) {
    if (this.removed) return;
    // trail
    for (let i = 0; i < this.trail.length; i++) {
      const t = this.trail[i];
      const a = (i / this.trail.length) * 0.12;
      ctx.beginPath();
      ctx.arc(t.x, t.y, this.radius * 0.5, 0, Math.PI * 2);
      ctx.fillStyle = this.color + Math.floor(a * 255).toString(16).padStart(2, '0');
      ctx.fill();
    }
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.scale(this.scale, this.scale);
    ctx.globalAlpha = this.opacity;
    // dot
    ctx.beginPath();
    ctx.arc(0, 0, this.radius, 0, Math.PI * 2);
    ctx.fillStyle = this.color;
    ctx.fill();
    // work ring
    if (this.beingWorkedOn && this.ringPulse > 0) {
      const ringR = this.radius + 4 + Math.sin(this.ringPulse) * 2;
      ctx.beginPath();
      ctx.arc(0, 0, ringR, 0, Math.PI * 2);
      ctx.strokeStyle = this.workType === 'remove' ? COLORS.removed :
        this.workType === 'enrich' ? COLORS.tealLight :
        this.workType === 'validate' ? COLORS.success : COLORS.worker;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = 0.4 + Math.sin(this.ringPulse * 2) * 0.2;
      ctx.stroke();
    }
    // settled glow
    if ((this.workDone || this.phase === 'complete') && !this.beingWorkedOn) {
      ctx.beginPath();
      ctx.arc(0, 0, this.radius + 2, 0, Math.PI * 2);
      const glow = ctx.createRadialGradient(0, 0, this.radius, 0, 0, this.radius + 5);
      glow.addColorStop(0, this.color + '30');
      glow.addColorStop(1, this.color + '00');
      ctx.fillStyle = glow;
      ctx.globalAlpha = 0.4;
      ctx.fill();
    }
    ctx.restore();
  }
}

// ── WORKER AGENT (hexagonal) ──
class WorkerAgent {
  id: number;
  name: string;
  canvasW: number;
  canvasH: number;
  homeX: number;
  homeY: number;
  x: number;
  y: number;
  targetParticle: Particle | null;
  state: 'idle' | 'moving' | 'working' | 'returning';
  action: string | null;
  radius: number;
  rotation: number;
  pulsePhase: number;
  idleWobble: number;
  trail: { x: number; y: number }[];
  workTimer: number;
  workDuration: number;
  onComplete: (() => void) | null;

  constructor(id: number, name: string, canvasW: number, canvasH: number) {
    this.id = id;
    this.name = name;
    this.canvasW = canvasW;
    this.canvasH = canvasH;
    this.homeX = canvasW * 0.25 + (id % 2) * canvasW * 0.5;
    this.homeY = canvasH * 0.3 + Math.floor(id / 2) * canvasH * 0.35;
    this.x = this.homeX;
    this.y = this.homeY;
    this.targetParticle = null;
    this.state = 'idle';
    this.action = null;
    this.radius = 7;
    this.rotation = 0;
    this.pulsePhase = Math.random() * Math.PI * 2;
    this.idleWobble = Math.random() * Math.PI * 2;
    this.trail = [];
    this.workTimer = 0;
    this.workDuration = 0;
    this.onComplete = null;
  }

  get busy() { return this.state !== 'idle'; }

  assignTask(particle: Particle, action: string, onComplete: () => void) {
    this.targetParticle = particle;
    this.action = action;
    this.state = 'moving';
    this.onComplete = onComplete;
    particle.beingWorkedOn = true;
    particle.workType = action;
    this.workDuration = 50 + Math.random() * 80;
    this.workTimer = 0;
  }

  update() {
    this.rotation += 0.03;
    this.pulsePhase += 0.06;
    this.idleWobble += 0.012;
    this.trail.push({ x: this.x, y: this.y });
    if (this.trail.length > 10) this.trail.shift();

    if (this.state === 'idle') {
      this.x = this.homeX + Math.sin(this.idleWobble) * 6;
      this.y = this.homeY + Math.cos(this.idleWobble * 0.7) * 6;
    } else if (this.state === 'moving') {
      if (!this.targetParticle || this.targetParticle.removed) { this.finish(); return; }
      this.x = lerp(this.x, this.targetParticle.x, 0.07);
      this.y = lerp(this.y, this.targetParticle.y, 0.07);
      if (dist(this.x, this.y, this.targetParticle.x, this.targetParticle.y) < 12) {
        this.state = 'working';
        this.workTimer = 0;
      }
    } else if (this.state === 'working') {
      if (!this.targetParticle || this.targetParticle.removed) { this.finish(); return; }
      this.workTimer++;
      const tx = this.targetParticle.x;
      const ty = this.targetParticle.y;
      const orbit = 13 + Math.sin(this.pulsePhase * 0.5) * 2;
      this.x = tx + Math.cos(this.rotation * 2) * orbit;
      this.y = ty + Math.sin(this.rotation * 2) * orbit;
      if (this.workTimer >= this.workDuration) {
        if (this.targetParticle) {
          this.targetParticle.beingWorkedOn = false;
          this.targetParticle.workDone = true;
        }
        this.finish();
      }
    } else if (this.state === 'returning') {
      this.x = lerp(this.x, this.homeX, 0.05);
      this.y = lerp(this.y, this.homeY, 0.05);
      if (dist(this.x, this.y, this.homeX, this.homeY) < 4) {
        this.state = 'idle';
      }
    }
  }

  finish() {
    this.state = 'returning';
    const cb = this.onComplete;
    this.targetParticle = null;
    this.onComplete = null;
    if (cb) cb();
  }

  draw(ctx: CanvasRenderingContext2D) {
    // trail
    for (let i = 1; i < this.trail.length; i++) {
      const t = this.trail[i];
      const prev = this.trail[i - 1];
      const a = (i / this.trail.length) * 0.12;
      ctx.beginPath();
      ctx.moveTo(prev.x, prev.y);
      ctx.lineTo(t.x, t.y);
      ctx.strokeStyle = COLORS.worker + Math.floor(a * 255).toString(16).padStart(2, '0');
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
    // connection line to target
    if ((this.state === 'moving' || this.state === 'working') && this.targetParticle && !this.targetParticle.removed) {
      ctx.beginPath();
      ctx.moveTo(this.x, this.y);
      ctx.lineTo(this.targetParticle.x, this.targetParticle.y);
      ctx.strokeStyle = 'rgba(0,160,160,0.1)';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.rotate(this.rotation);
    // glow when working
    if (this.state === 'working') {
      ctx.beginPath();
      ctx.arc(0, 0, this.radius + 5 + Math.sin(this.pulsePhase) * 2, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0,128,128,0.12)';
      ctx.fill();
    }
    // hexagon body
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 6;
      const px = Math.cos(angle) * this.radius;
      const py = Math.sin(angle) * this.radius;
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fillStyle = this.state === 'working' ? COLORS.teal :
      this.state === 'moving' ? COLORS.tealLight + 'CC' : COLORS.teal + '55';
    ctx.fill();
    ctx.strokeStyle = COLORS.tealLight;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    // center dot
    ctx.beginPath();
    ctx.arc(0, 0, 2.5, 0, Math.PI * 2);
    ctx.fillStyle = '#FFF';
    ctx.globalAlpha = this.state === 'idle' ? 0.35 : 0.9;
    ctx.fill();
    // name label
    ctx.rotate(-this.rotation);
    ctx.globalAlpha = this.state === 'idle' ? 0.25 : 0.6;
    ctx.font = "9px 'DM Sans', system-ui, sans-serif";
    ctx.fillStyle = COLORS.tealLight;
    ctx.textAlign = 'center';
    ctx.fillText(this.name, 0, this.radius + 14);
    ctx.restore();
  }
}

// ── Animation state ──
interface AnimState {
  particles: Particle[];
  workers: WorkerAgent[];
  phase: string;
  gridAlpha: number;
  taskQueue: { action: string; recordId?: number }[];
  actionInterval: ReturnType<typeof setInterval> | null;
}

// ── PROPS ──
export interface PipelineAnimationProps {
  /** Current phase from polling: sync, discovery, approval, transform, gold */
  currentPhase: string | null;
  /** Phase statuses from polling */
  phases: Record<string, { status: string }> | null;
  /** Overall progress 0-100 */
  overallProgress: number;
  /** Pipeline status: running, completed, failed, awaiting_approval */
  pipelineStatus: string | null;
  /** Phase description text from backend */
  phaseDescription?: string;
}

const PipelineAnimation: React.FC<PipelineAnimationProps> = ({
  currentPhase,
  phases,
  overallProgress,
  pipelineStatus,
  phaseDescription,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef<AnimState>({
    particles: [],
    workers: [],
    phase: 'waiting',
    gridAlpha: 0,
    taskQueue: [],
    actionInterval: null,
  });
  const animFrameRef = useRef<number | null>(null);
  const lastTimeRef = useRef(0);
  const prevPhaseRef = useRef<string | null>(null);
  const [animPhase, setAnimPhase] = useState('waiting');
  const [counts, setCounts] = useState({ total: 0, moved: 0, enriched: 0, removed: 0, validated: 0 });

  // ── Dispatch a single "work" action to an idle worker ──
  const dispatchAction = useCallback((action: string) => {
    const s = stateRef.current;
    const worker = s.workers.find(w => !w.busy);
    if (!worker) {
      s.taskQueue.push({ action });
      return;
    }
    const available = s.particles.filter(p => !p.removed && !p.beingWorkedOn && !p.workDone);
    if (available.length === 0) return;

    const target = available[Math.floor(Math.random() * available.length)];

    if (action === 'move') {
      const cols = Math.floor((target.canvasW - 80) / 28);
      const rows = Math.floor((target.canvasH - 100) / 28);
      target.targetX = 40 + Math.floor(Math.random() * cols) * 28;
      target.targetY = 50 + Math.floor(Math.random() * rows) * 28;
    }

    worker.assignTask(target, action, () => {
      setCounts(prev => {
        const next = { ...prev };
        if (action === 'move') next.moved++;
        if (action === 'enrich') next.enriched++;
        if (action === 'remove' || action === 'merge') next.removed++;
        if (action === 'validate') next.validated++;
        return next;
      });
      // Process queued tasks
      if (s.taskQueue.length > 0) {
        const next = s.taskQueue.shift()!;
        setTimeout(() => dispatchAction(next.action), 50);
      }
    });
  }, []);

  // ── Start continuous action dispatch for transform/gold phases ──
  const startActionLoop = useCallback((phase: string) => {
    const s = stateRef.current;
    if (s.actionInterval) clearInterval(s.actionInterval);

    const actions = phase === 'gold'
      ? ['validate', 'enrich', 'validate', 'validate']
      : ['move', 'enrich', 'remove', 'merge', 'move', 'enrich', 'move', 'validate'];

    s.actionInterval = setInterval(() => {
      const action = actions[Math.floor(Math.random() * actions.length)];
      dispatchAction(action);
    }, 300 + Math.random() * 400);
  }, [dispatchAction]);

  const stopActionLoop = useCallback(() => {
    const s = stateRef.current;
    if (s.actionInterval) {
      clearInterval(s.actionInterval);
      s.actionInterval = null;
    }
  }, []);

  // ── React to phase changes from polling ──
  // v3.11b - Also detect active phase from phases prop (fallback if currentPhase lags)
  useEffect(() => {
    // Determine the effective phase: prefer currentPhase, fall back to scanning phases
    let effectivePhase = currentPhase;
    if (phases) {
      // Find the latest running phase from phases data
      const phaseOrder = ['sync', 'discovery', 'approval', 'transform', 'gold'];
      for (let i = phaseOrder.length - 1; i >= 0; i--) {
        const ps = phases[phaseOrder[i]]?.status;
        if (ps === 'running' || ps === 'awaiting_approval') {
          effectivePhase = phaseOrder[i];
          break;
        }
      }
    }

    if (!effectivePhase || effectivePhase === prevPhaseRef.current) return;
    prevPhaseRef.current = effectivePhase;

    const s = stateRef.current;
    const animKey = BACKEND_TO_ANIM[effectivePhase] || effectivePhase;

    s.phase = animKey;
    setAnimPhase(animKey);
    s.particles.forEach(p => { p.phase = animKey; });

    stopActionLoop();

    if (animKey === 'transform' || animKey === 'gold') {
      startActionLoop(animKey);
    }

    if (animKey === 'complete' || pipelineStatus === 'completed') {
      s.particles.forEach(p => { p.workDone = true; p.phase = 'complete'; });
      s.phase = 'complete';
      setAnimPhase('complete');
    }
  }, [currentPhase, phases, pipelineStatus, startActionLoop, stopActionLoop]);

  // ── Handle completion/failure from pipelineStatus ──
  useEffect(() => {
    if (pipelineStatus === 'completed') {
      const s = stateRef.current;
      stopActionLoop();
      s.particles.forEach(p => { p.workDone = true; p.phase = 'complete'; });
      s.phase = 'complete';
      setAnimPhase('complete');
    } else if (pipelineStatus === 'failed') {
      stopActionLoop();
      setAnimPhase('failed');
    }
  }, [pipelineStatus, stopActionLoop]);

  // ── Initialize canvas + render loop ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const container = canvas.parentElement;
    if (!container) return;

    const resize = () => {
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
    };
    resize();

    const w = canvas.width;
    const h = canvas.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Create particles
    const cols = Math.floor((w - 80) / 28);
    const rows = Math.floor((h - 100) / 28);
    const count = Math.min(cols * rows, 150);
    const particles: Particle[] = [];
    for (let i = 0; i < count; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const gx = 40 + col * 28;
      const gy = 50 + row * 28;
      const cat = CATEGORY_KEYS[Math.floor(Math.random() * CATEGORY_KEYS.length)];
      particles.push(new Particle(w, h, gx, gy, cat, i));
    }

    // Set initial phase based on current polling state
    const initPhase = currentPhase ? (BACKEND_TO_ANIM[currentPhase] || 'sync') : 'sync';
    particles.forEach(p => { p.phase = initPhase; });

    // Create workers
    const workers = WORKER_NAMES.map((name, i) => new WorkerAgent(i, name, w, h));

    stateRef.current = {
      particles,
      workers,
      phase: initPhase,
      gridAlpha: 0,
      taskQueue: [],
      actionInterval: null,
    };

    setCounts({ total: count, moved: 0, enriched: 0, removed: 0, validated: 0 });
    setAnimPhase(initPhase);
    prevPhaseRef.current = currentPhase;

    // Start action loop if already in transform/gold
    if (initPhase === 'transform' || initPhase === 'gold') {
      startActionLoop(initPhase);
    }

    lastTimeRef.current = performance.now();

    const render = (timestamp: number) => {
      const dt = Math.min((timestamp - lastTimeRef.current) / 1000, 0.05);
      lastTimeRef.current = timestamp;
      const s = stateRef.current;

      ctx.clearRect(0, 0, w, h);
      // Background — MyLojic navy dark
      ctx.fillStyle = COLORS.navyDark;
      ctx.fillRect(0, 0, w, h);

      // Grid fade
      const targetGrid = s.phase === 'sync' || s.phase === 'waiting' ? 0 : 0.05;
      s.gridAlpha = lerp(s.gridAlpha, targetGrid, 0.02);
      if (s.gridAlpha > 0.005) {
        ctx.strokeStyle = `rgba(0, 128, 128, ${s.gridAlpha})`;
        ctx.lineWidth = 0.5;
        for (let x = 40; x < w - 30; x += 28) {
          ctx.beginPath(); ctx.moveTo(x, 40); ctx.lineTo(x, h - 40); ctx.stroke();
        }
        for (let y = 50; y < h - 30; y += 28) {
          ctx.beginPath(); ctx.moveTo(30, y); ctx.lineTo(w - 30, y); ctx.stroke();
        }
      }

      // Update & draw particles
      s.particles.forEach(p => { p.update(dt); p.draw(ctx); });

      // Update & draw workers (only during transform/gold/complete)
      if (s.phase === 'transform' || s.phase === 'gold' || s.phase === 'complete') {
        s.workers.forEach(wk => { wk.update(); wk.draw(ctx); });
      }

      // Complete glow
      if (s.phase === 'complete') {
        const grd = ctx.createRadialGradient(w / 2, h / 2, 30, w / 2, h / 2, w * 0.55);
        grd.addColorStop(0, 'rgba(76, 175, 80, 0.04)');
        grd.addColorStop(1, 'rgba(76, 175, 80, 0)');
        ctx.fillStyle = grd;
        ctx.fillRect(0, 0, w, h);
      }

      animFrameRef.current = requestAnimationFrame(render);
    };

    animFrameRef.current = requestAnimationFrame(render);

    const onResize = () => {
      resize();
      // Don't recreate particles on resize — just let them continue
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      stopActionLoop();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // ── v3.11c - Compute active phase from phases prop (same as old MUI Stepper) ──
  const phaseKeys = ['sync', 'discovery', 'approval', 'transform', 'gold'];

  // Find the currently active phase from real polling data
  const getActivePhaseKey = (): string => {
    if (pipelineStatus === 'completed') return 'complete';
    if (pipelineStatus === 'failed') return 'failed';
    if (!phases) return animPhase;
    // Find latest running/awaiting phase
    for (let i = phaseKeys.length - 1; i >= 0; i--) {
      const ps = phases[phaseKeys[i]]?.status;
      if (ps === 'running' || ps === 'awaiting_approval') return phaseKeys[i];
    }
    // If nothing running, find latest completed and show next as pending
    for (let i = phaseKeys.length - 1; i >= 0; i--) {
      if (phases[phaseKeys[i]]?.status === 'completed') {
        return i < phaseKeys.length - 1 ? phaseKeys[i + 1] : 'complete';
      }
    }
    return animPhase;
  };

  const activeKey = getActivePhaseKey();
  const activeMeta = PHASE_META[activeKey] || PHASE_META.waiting;

  return (
    <div style={{
      width: '100%',
      height: '420px',
      background: COLORS.navyDark,
      display: 'flex',
      fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
      color: '#E2E8F0',
      overflow: 'hidden',
      borderRadius: '12px',
      border: `1px solid ${COLORS.navy}`,
    }}>
      {/* Canvas area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px 16px 16px 20px', minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div>
            <div style={{ fontSize: '11px', color: '#94A3B8', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Data Pipeline</div>
            <div style={{ fontSize: '18px', fontWeight: 600, color: '#F1F5F9' }}>Preparing Your Data</div>
          </div>
          {/* v3.11c - Phase dots driven by phases prop */}
          <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
            {phaseKeys.map((p, i) => {
              const ps = phases?.[p]?.status;
              const isDone = ps === 'completed' || pipelineStatus === 'completed';
              const isActive = ps === 'running' || ps === 'awaiting_approval';
              return (
                <div key={p} style={{
                  width: isActive ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  background: isDone ? COLORS.success
                    : isActive ? activeMeta.color
                    : COLORS.navy,
                  transition: 'all 0.5s ease',
                }} />
              );
            })}
          </div>
        </div>
        <div style={{
          flex: 1, borderRadius: '10px', overflow: 'hidden',
          border: `1px solid ${COLORS.navy}`, position: 'relative',
        }}>
          <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
          {/* v3.11c - Phase label overlay driven by phases prop */}
          <div style={{
            position: 'absolute', bottom: '12px', left: '12px',
            background: 'rgba(0,34,68,0.88)', borderRadius: '8px', padding: '8px 14px',
            border: `1px solid ${COLORS.navy}`,
          }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: activeMeta.color, marginBottom: '2px' }}>
              {activeMeta.label}
            </div>
            <div style={{ fontSize: '11px', color: '#94A3B8' }}>
              {phaseDescription || activeMeta.desc}
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar — v3.11c: removed Workers + Data Types to fix scrollbar overflow */}
      <div style={{
        width: '200px', padding: '16px', borderLeft: `1px solid ${COLORS.navy}`,
        display: 'flex', flexDirection: 'column', gap: '16px',
        background: COLORS.navyDark, flexShrink: 0,
      }}>
        {/* Progress */}
        <div>
          <div style={{ fontSize: '10px', color: '#94A3B8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>
            Overall Progress
          </div>
          <div style={{ marginBottom: '6px' }}>
            <span style={{ fontSize: '22px', fontWeight: 700, color: activeMeta.color }}>{overallProgress}%</span>
          </div>
          <div style={{ height: '4px', borderRadius: '2px', background: COLORS.navy, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: '2px', background: activeMeta.color,
              width: `${overallProgress}%`, transition: 'width 0.5s ease, background 0.5s ease',
            }} />
          </div>
        </div>

        {/* Pipeline stages — driven directly from phases prop */}
        <div>
          <div style={{ fontSize: '10px', color: '#94A3B8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>
            Pipeline Stages
          </div>
          {phaseKeys.map((p) => {
            const pm = PHASE_META[p];
            const phaseStatus = phases?.[p]?.status;
            const isDone = phaseStatus === 'completed' || pipelineStatus === 'completed';
            const isRunning = phaseStatus === 'running';
            const isAwaiting = phaseStatus === 'awaiting_approval';
            const isActive = isRunning || isAwaiting;
            return (
              <div key={p} style={{
                display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0',
                opacity: isDone ? 0.5 : isActive ? 1 : 0.25,
                transition: 'opacity 0.5s ease',
              }}>
                <div style={{
                  width: '7px', height: '7px', borderRadius: '50%',
                  background: isDone ? COLORS.success : isAwaiting ? COLORS.warning : isRunning ? pm.color : COLORS.navy,
                  boxShadow: isActive ? `0 0 8px ${pm.color}60` : 'none',
                  transition: 'all 0.5s ease',
                }} />
                <span style={{
                  fontSize: '12px',
                  color: isActive ? '#F1F5F9' : '#94A3B8',
                  fontWeight: isActive ? 600 : 400,
                  flex: 1,
                }}>
                  {pm.label}
                </span>
                {isDone && <span style={{ fontSize: '10px', color: COLORS.success }}>&#10003;</span>}
                {isAwaiting && <span style={{ fontSize: '10px', color: COLORS.warning }}>&#9679;</span>}
              </div>
            );
          })}
        </div>

        {/* Records */}
        <div>
          <div style={{ fontSize: '10px', color: '#94A3B8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>
            Records
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {[
              { label: 'Total', value: counts.total, color: '#F1F5F9' },
              { label: 'Moved', value: counts.moved, color: COLORS.orange },
              { label: 'Enriched', value: counts.enriched, color: COLORS.tealLight },
              { label: 'Cleaned', value: counts.removed, color: COLORS.error },
              { label: 'Validated', value: counts.validated, color: COLORS.success },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                <span style={{ color: r.color === '#F1F5F9' ? '#94A3B8' : r.color }}>{r.label}</span>
                <span style={{ color: r.color, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{r.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PipelineAnimation;
